import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  FaTruck, FaPlus, FaSyncAlt, FaCopy, FaExternalLinkAlt, FaCog,
  FaBoxOpen, FaCheckCircle, FaExclamationTriangle, FaFileImport, FaFileExcel,
} from 'react-icons/fa';
import { Button, Badge, DataTable, StatCard, Modal, ConfirmDialog, Input } from '../../../Components/UI';
import { END_POINT, getJwtToken } from '../../../Redux/AdminReducer/action';
import usePincodeLookup from '../../../hooks/usePincodeLookup';
import exportToExcel from '../../../UtilityHookFunctions/exportToExcel';
import {
  fetchShipments, createShipment, bookShipment, trackShipment,
  refreshAllShipments, cancelShipment, fetchCourierSettings, backfillFromPrescriptions,
  saveCourierSettings, buildPatientTrackingLink, buildDtdcTrackingLink, buildIndiaPostTrackingLink, buildCourierTrackingLink,
} from '../../../Services/courier';
import TrackingTimeline, {
  ShipmentProgress, STATUS_LABELS, STATUS_VARIANTS, formatScanTime,
} from './TrackingTimeline';

const authHeaders = () => ({ headers: { Authorization: getJwtToken() } });

const STATUS_FILTER_OPTIONS = [
  'Draft', 'Booked', 'PickedUp', 'InTransit', 'OutForDelivery',
  'Delivered', 'Exception', 'RTO', 'Cancelled',
].map((value) => ({ value, label: STATUS_LABELS[value] || value }));

// DTDC service codes available to a retail (B2C) account.
const SERVICE_TYPES = ['B2C PRIORITY', 'B2C SMART EXPRESS', 'EXPRESS', 'PREMIUM'];

const EMPTY_SHIPMENT = {
  patient: null,
  destination: {
    name: '', phone: '', alternatePhone: '', email: '',
    addressLine1: '', addressLine2: '', landmark: '',
    city: '', state: '', pincode: '', country: 'India',
  },
  serviceType: 'B2C PRIORITY',
  description: 'Homeopathic medicines',
  numPieces: 1,
  weight: 0.5,
  declaredValue: '',
  paymentMode: 'Prepaid',
  codAmount: '',
  notes: '',
};

const patientFullName = (p) =>
  [p?.firstName, p?.middleName, p?.surname].filter(Boolean).join(' ').trim();

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="mb-1.5 block text-xs font-semibold tracking-wide text-slate-700">{label}</label>
    {children}
  </div>
);

const Select = (props) => (
  <select
    {...props}
    className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition hover:border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
  />
);

export default function CourierList() {
  const [shipments, setShipments] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Create flow
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_SHIPMENT);
  const [patients, setPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Tracking drawer
  const [tracked, setTracked] = useState(null);
  const [tracking, setTracking] = useState(false);

  // Settings
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsProviderTab, setSettingsProviderTab] = useState('DTDC');
  const [settings, setSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [importing, setImporting] = useState(false);

  const loadShipments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchShipments({
        page, limit,
        search: search || undefined,
        status: statusFilter || undefined,
      });
      setShipments(data?.data || []);
      setCounts(data?.counts || {});
      setTotalPages(data?.totalPages || 1);
      setTotal(data?.total || 0);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not load shipments');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, statusFilter]);

  // Debounced so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(loadShipments, 300);
    return () => clearTimeout(timer);
  }, [loadShipments]);

  // Courier capability drives whether "Book now" is even offered.
  useEffect(() => {
    fetchCourierSettings()
      .then((res) => setSettings({ ...res?.data, ...res?.status }))
      .catch(() => setSettings(null));
  }, []);

  // Patient lookup inside the create flow (debounced)
  useEffect(() => {
    if (!createOpen || form.patient) return undefined;

    setPatientsLoading(true);
    const timer = setTimeout(() => {
      const params = new URLSearchParams({ limit: 20, page: 1 });
      if (patientSearch) params.append('search', patientSearch);

      axios
        .get(`${END_POINT}/customer/list/dashboard?${params.toString()}`, authHeaders())
        .then(({ data }) => setPatients(Array.isArray(data) ? data : data?.data || []))
        .catch(() => toast.error('Could not load patients'))
        .finally(() => setPatientsLoading(false));
    }, 300);

    return () => clearTimeout(timer);
  }, [patientSearch, createOpen, form.patient]);

  const openCreate = () => {
    setForm(EMPTY_SHIPMENT);
    setPatientSearch('');
    setPatients([]);
    setCreateOpen(true);
  };

  /** Seed the delivery address from what the patient record already holds. */
  const choosePatient = (p) => {
    setForm((prev) => ({
      ...prev,
      patient: {
        id: p._id,
        name: patientFullName(p),
        mobile: p.mobile || '',
        email: p.email || '',
        patientId: p.patientId || p.customerId || '',
      },
      destination: {
        ...prev.destination,
        name: patientFullName(p),
        phone: p.mobile || '',
        email: p.email || '',
        addressLine1: p.location || '',
        city: p.city || '',
        state: p.state || '',
        pincode: p.pincode ? String(p.pincode) : '',
        country: p.country || 'India',
      },
    }));
  };

  const setDestination = (key) => (e) =>
    setForm((prev) => ({ ...prev, destination: { ...prev.destination, [key]: e.target.value } }));

  const setField = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  // Auto-fill city / state / country once a 6-digit pincode is entered
  const handlePincodeResult = useCallback((details) => {
    setForm((prev) => ({
      ...prev,
      destination: {
        ...prev.destination,
        city: details.city || prev.destination.city,
        state: details.state || prev.destination.state,
        country: details.country || prev.destination.country,
      },
    }));
  }, []);
  const { onPincodeChange, loading: pincodeLoading, error: pincodeError } = usePincodeLookup(handlePincodeResult);

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      // Pull every shipment matching the current search / status filter, not just this page.
      let rows = shipments;
      try {
        const data = await fetchShipments({
          page: 1,
          limit: Math.max(total, shipments.length, 1),
          search: search || undefined,
          status: statusFilter || undefined,
        });
        if (Array.isArray(data?.data)) rows = data.data;
      } catch {
        // fall back to the rows already on screen
      }
      if (!rows.length) {
        toast.info('No shipments to export');
        return;
      }
      const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB') : '');
      exportToExcel(
        rows,
        `Courier_Shipments_${new Date().toISOString().slice(0, 10)}`,
        [
          { key: 'referenceNumber', label: 'Reference No.' },
          { key: 'provider', label: 'Courier', format: (v) => v || 'DTDC' },
          { key: 'consignmentNumber', label: 'AWB / Consignment No.', format: (v) => v || '' },
          { key: 'patient.name', label: 'Patient', format: (v) => v || '' },
          { key: 'destination.name', label: 'Recipient', format: (v) => v || '' },
          { key: 'destination.phone', label: 'Phone', format: (v) => v || '' },
          {
            key: 'destination',
            label: 'Address',
            format: (d) => [d?.addressLine1, d?.addressLine2, d?.landmark].filter(Boolean).join(', '),
          },
          { key: 'destination.city', label: 'City', format: (v) => v || '' },
          { key: 'destination.state', label: 'State', format: (v) => v || '' },
          { key: 'destination.pincode', label: 'Pincode', format: (v) => (v ? String(v) : '') },
          { key: 'status', label: 'Status', format: (v) => STATUS_LABELS[v] || v || '' },
          { key: 'paymentMode', label: 'Payment', format: (v) => v || 'Prepaid' },
          { key: 'codAmount', label: 'COD Amount', format: (v, row) => (row.paymentMode === 'COD' ? Number(v) || 0 : '') },
          { key: 'createdAt', label: 'Created', format: fmtDate },
          { key: 'lastTrackedAt', label: 'Last Tracked', format: fmtDate },
        ],
        'Shipments'
      );
    } finally {
      setExporting(false);
    }
  };

  const submitShipment = async (book) => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        book,
        numPieces: Number(form.numPieces) || 1,
        weight: Number(form.weight) || 0.5,
        declaredValue: Number(form.declaredValue) || 0,
        codAmount: form.paymentMode === 'COD' ? Number(form.codAmount) || 0 : 0,
      };
      const res = await createShipment(payload);
      toast.success(
        book
          ? `Booked with DTDC — ${res?.data?.consignmentNumber}`
          : 'Shipment saved as draft'
      );
      setCreateOpen(false);
      loadShipments();
    } catch (error) {
      // A failed booking still leaves a draft behind, so say so.
      const message = error?.response?.data?.message || 'Could not save the shipment';
      toast.error(message);
      if (error?.response?.data?.data) loadShipments();
    } finally {
      setSaving(false);
    }
  };

  const handleBook = async (shipment) => {
    try {
      const res = await bookShipment(shipment._id);
      toast.success(`Booked with DTDC — ${res?.data?.consignmentNumber}`);
      loadShipments();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'DTDC booking failed');
    }
  };

  /** Live track: hits DTDC, stores the scans, and opens the drawer. */
  const handleTrack = async (shipment, { silent = false } = {}) => {
    if (!silent) setTracked(shipment);
    setTracking(true);
    try {
      const res = await trackShipment(shipment._id);
      setTracked(res?.data || shipment);
      setShipments((prev) => prev.map((s) => (s._id === res?.data?._id ? res.data : s)));
    } catch (error) {
      if (!silent) toast.error(error?.response?.data?.message || 'Could not reach DTDC tracking');
    } finally {
      setTracking(false);
    }
  };

  // Keep the open drawer live without the user pressing anything.
  useEffect(() => {
    if (!tracked?._id || !tracked?.consignmentNumber) return undefined;
    if (['Delivered', 'Cancelled', 'Failed', 'RTO'].includes(tracked.status)) return undefined;

    const timer = setInterval(() => handleTrack(tracked, { silent: true }), 60000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracked?._id, tracked?.status]);

  const handleBackfill = async () => {
    setImporting(true);
    try {
      const result = await backfillFromPrescriptions();
      if (!result?.created && !result?.updated) toast.info('No prescription courier entries left to import');
      else toast.success(`Imported ${result.created} new, updated ${result.updated} shipment(s)`);
      loadShipments();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not import from prescriptions');
    } finally {
      setImporting(false);
    }
  };

  const handleRefreshAll = async () => {
    setRefreshing(true);
    try {
      const res = await refreshAllShipments(50);
      if (!res?.refreshed && !res?.failed) toast.info('Nothing in transit to refresh');
      else toast.success(`Refreshed ${res.refreshed} shipment${res.refreshed === 1 ? '' : 's'}${res.failed ? `, ${res.failed} failed` : ''}`);
      loadShipments();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not refresh shipments');
    } finally {
      setRefreshing(false);
    }
  };

  const confirmCancel = async () => {
    setCancelling(true);
    try {
      await cancelShipment(cancelTarget._id, 'Cancelled by clinic');
      toast.success('Consignment cancelled');
      setCancelTarget(null);
      setTracked(null);
      loadShipments();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not cancel the consignment');
    } finally {
      setCancelling(false);
    }
  };

  const copyPatientLink = async (shipment) => {
    const link = buildPatientTrackingLink(shipment.trackingToken);
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Tracking link copied');
    } catch (e) {
      window.prompt('Copy the tracking link:', link);
    }
  };

  const isMainDoctor = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("clinic-application-jwt");
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      return parsed?.role === 'MainDoctor' || parsed?.data?.role === 'MainDoctor' || parsed?.user?.role === 'MainDoctor';
    } catch {
      return false;
    }
  }, []);

  const openSettings = async (provider = 'DTDC') => {
    try {
      setSettingsProviderTab(provider);
      const res = await fetchCourierSettings({ provider });
      const provData = res?.data?.[provider === 'India Post' ? 'IndiaPost' : 'DTDC'] || res?.data || {};
      const provStatus = res?.status?.[provider === 'India Post' ? 'IndiaPost' : 'DTDC'] || res?.status || {};
      setSettings({ ...provData, ...provStatus });
      setSettingsForm({
        provider,
        enabled: provData?.enabled || false,
        environment: provData?.environment || 'live',
        customerCode: provData?.customerCode || '',
        apiKey: '',
        trackingUsername: provData?.trackingUsername || '',
        trackingPassword: '',
        defaultServiceType: provData?.defaultServiceType || (provider === 'India Post' ? 'SPEED POST' : 'B2C PRIORITY'),
        origin: {
          name: '', phone: '', addressLine1: '', addressLine2: '',
          city: '', state: '', pincode: '', country: 'India',
          ...(provData?.origin || {}),
        },
      });
      setSettingsOpen(true);
    } catch (error) {
      toast.error('Could not load courier settings');
    }
  };

  const submitSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await saveCourierSettings({ ...settingsForm, provider: settingsProviderTab });
      const provData = res?.data || {};
      const provStatus = res?.status || {};
      setSettings({ ...provData, ...provStatus });
      toast.success(`${settingsProviderTab} settings saved`);
      setSettingsOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not save courier settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const inTransitCount = useMemo(
    () => ['Booked', 'PickedUp', 'InTransit', 'OutForDelivery'].reduce((n, k) => n + (counts[k] || 0), 0),
    [counts]
  );

  const headers = [
    {
      label: 'Shipment',
      key: 'referenceNumber',
      render: (value, row) => (
        <div>
          <p className="font-semibold text-slate-800">{value}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-semibold text-slate-700 border border-slate-200">
              {row.provider || 'DTDC'}
            </span>
            <span className="text-xs text-slate-500">
              {row.consignmentNumber ? `AWB ${row.consignmentNumber}` : 'Not booked yet'}
            </span>
          </div>
        </div>
      ),
    },
    {
      label: 'Patient',
      key: 'patient',
      render: (patient, row) => (
        <div>
          <p className="text-slate-800">{patient?.name || '—'}</p>
          <p className="text-xs text-slate-500">
            {[row.destination?.city, row.destination?.pincode].filter(Boolean).join(' · ')}
          </p>
        </div>
      ),
    },
    {
      label: 'Status',
      key: 'status',
      render: (value, row) => (
        <div className="space-y-1">
          <Badge variant={STATUS_VARIANTS[value] || 'default'}>{STATUS_LABELS[value] || value}</Badge>
          {row.lastTrackedAt && (
            <p className="text-[11px] text-slate-400">Checked {formatScanTime(row.lastTrackedAt)}</p>
          )}
        </div>
      ),
    },
    {
      label: 'Payment',
      key: 'paymentMode',
      render: (value, row) =>
        value === 'COD' ? `COD ₹${row.codAmount || 0}` : 'Prepaid',
    },
    {
      label: 'Created',
      key: 'createdAt',
      render: (value) => (value ? new Date(value).toLocaleDateString('en-GB') : '—'),
    },
  ];

  const rowActions = (row) => (
    <>
      {!row.consignmentNumber && row.status === 'Draft' ? (
        <Button
          size="xs"
          onClick={() => handleBook(row)}
          disabled={settings && settings.canBook === false}
          title={settings?.canBook === false ? 'Add courier booking credentials first' : 'Send to courier'}
          icon={<FaBoxOpen className="h-3.5 w-3.5" />}
        >
          Book
        </Button>
      ) : (
        <Button
          size="xs"
          onClick={() => handleTrack(row)}
          icon={<FaTruck className="h-3.5 w-3.5" />}
        >
          Track
        </Button>
      )}
      {row.consignmentNumber ? (
        <Button
          size="xs"
          variant="outline"
          onClick={() => {
            const url = buildCourierTrackingLink(row.provider, row.consignmentNumber);
            window.open(url, '_blank', 'noopener,noreferrer');
          }}
          icon={<FaExternalLinkAlt className="h-3.5 w-3.5" />}
          title="Open official tracking website"
        >
          Web
        </Button>
      ) : null}
      <Button
        size="xs"
        variant="outline"
        disabled={!row.trackingToken}
        onClick={() => copyPatientLink(row)}
        icon={<FaCopy className="h-3.5 w-3.5" />}
      >
        Link
      </Button>
    </>
  );

  const credentialsMissing = settings && (settings.canBook === false || settings.canTrack === false);

  return (
    <div className="w-full space-y-6 p-4 md:p-8">
      <div className="flex flex-col gap-4 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold tracking-tight text-slate-800">
            <FaTruck className="text-sky-600" /> Courier &amp; Dispatch
          </h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Track DTDC and India Post medicine dispatches and patient parcels live
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isMainDoctor && (
            <Button variant="outline" size="sm" onClick={() => openSettings('DTDC')} icon={<FaCog className="h-3.5 w-3.5" />}>
              Courier settings
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshAll}
            isLoading={refreshing}
            icon={<FaSyncAlt className="h-3.5 w-3.5" />}
          >
            Refresh tracking
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleBackfill}
            isLoading={importing}
            icon={<FaFileImport className="h-3.5 w-3.5" />}
          >
            Import from prescriptions
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            isLoading={exporting}
            icon={<FaFileExcel className="h-3.5 w-3.5" />}
          >
            Export to Excel
          </Button>
          <Button size="sm" onClick={openCreate} icon={<FaPlus className="h-3.5 w-3.5" />}>
            New shipment
          </Button>
        </div>
      </div>

      {credentialsMissing && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <FaExclamationTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold">DTDC account not fully configured</p>
            <p className="text-xs">
              {settings?.canBook === false && 'Booking needs an API key and customer code. '}
              {settings?.canTrack === false && 'Live tracking needs a tracking token or username and password. '}
              Add them under “DTDC settings”.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Total shipments" value={total} icon={<FaTruck />} color="sky" />
        <StatCard title="In transit" value={inTransitCount} icon={<FaTruck />} color="amber" />
        <StatCard title="Delivered" value={counts.Delivered || 0} icon={<FaCheckCircle />} color="emerald" />
        <StatCard
          title="Needs attention"
          value={(counts.Exception || 0) + (counts.RTO || 0)}
          icon={<FaExclamationTriangle />}
          color="rose"
        />
      </div>

      <DataTable
        headers={headers}
        data={shipments}
        actions={rowActions}
        isLoading={loading}
        title="Shipments"
        subtitle="Every parcel dispatched to a patient"
        page={page}
        totalPages={totalPages}
        total={total}
        rowsPerPage={limit}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => { setLimit(value); setPage(1); }}
        onSearch={(value) => { setSearch(value); setPage(1); }}
        searchValue={search}
        searchPlaceholder="Search reference, AWB, patient or pincode..."
        filters={[{ key: 'status', label: 'All statuses', value: statusFilter, options: STATUS_FILTER_OPTIONS }]}
        onFilter={(key, value) => { if (key === 'status') { setStatusFilter(value); setPage(1); } }}
        emptyMessage="No shipments yet."
        emptyAction={<Button size="sm" onClick={openCreate}>Book the first shipment</Button>}
      />

      {/* ---------------- Create shipment ---------------- */}
      <Modal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New shipment"
        subtitle={form.patient ? `Dispatching to ${form.patient.name}` : 'Step 1 — choose the patient'}
        maxWidth="max-w-3xl"
        footer={
          form.patient ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => setForm(EMPTY_SHIPMENT)}>
                Change patient
              </Button>
              <Button variant="outline" size="sm" onClick={() => submitShipment(false)} isLoading={saving}>
                Save draft
              </Button>
              <Button
                size="sm"
                onClick={() => submitShipment(true)}
                isLoading={saving}
                disabled={settings && settings.canBook === false}
              >
                Book with DTDC
              </Button>
            </>
          ) : null
        }
      >
        {!form.patient ? (
          <>
            <input
              type="text"
              autoFocus
              value={patientSearch}
              placeholder="Search patient by name, mobile or email..."
              onChange={(e) => setPatientSearch(e.target.value)}
              className="mb-4 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
            />

            {patientsLoading && <p className="py-4 text-sm text-slate-500">Searching patients...</p>}
            {!patientsLoading && !patients.length && (
              <p className="py-4 text-sm text-slate-500">No patients found.</p>
            )}

            <div className="max-h-80 divide-y overflow-y-auto rounded-xl border border-slate-200">
              {patients.map((p) => (
                <button
                  key={p._id}
                  onClick={() => choosePatient(p)}
                  className="flex w-full items-center justify-between px-4 py-3 text-left transition hover:bg-sky-50"
                >
                  <span className="font-medium text-slate-800">{patientFullName(p) || 'Unnamed patient'}</span>
                  <span className="text-xs text-slate-500">{p.mobile}</span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="space-y-5">
            <section>
              <h4 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500">Delivery address</h4>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Input label="Recipient name" value={form.destination.name} onChange={setDestination('name')} required />
                <Input label="Phone" value={form.destination.phone} onChange={setDestination('phone')} required />
                <Input label="Address line 1" value={form.destination.addressLine1} onChange={setDestination('addressLine1')} className="md:col-span-2" required />
                <Input label="Address line 2" value={form.destination.addressLine2} onChange={setDestination('addressLine2')} className="md:col-span-2" />
                <Input label="Landmark" value={form.destination.landmark} onChange={setDestination('landmark')} />
                <Input label="City" value={form.destination.city} onChange={setDestination('city')} required />
                <Input label="State" value={form.destination.state} onChange={setDestination('state')} required />
                <Input
                  label="Pincode"
                  value={form.destination.pincode}
                  onChange={(e) => {
                    setDestination('pincode')(e);
                    onPincodeChange(e.target.value);
                  }}
                  helperText={pincodeLoading ? 'Looking up…' : null}
                  error={!pincodeLoading && pincodeError ? pincodeError : null}
                  required
                />
              </div>
            </section>

            <section>
              <h4 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500">Parcel</h4>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Field label="Service type">
                  <Select value={form.serviceType} onChange={setField('serviceType')}>
                    {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </Select>
                </Field>
                <Input label="Contents" value={form.description} onChange={setField('description')} />
                <Input label="Pieces" type="number" min="1" value={form.numPieces} onChange={setField('numPieces')} />
                <Input label="Weight (kg)" type="number" step="0.1" min="0.1" value={form.weight} onChange={setField('weight')} />
                <Input label="Declared value (₹)" type="number" min="0" value={form.declaredValue} onChange={setField('declaredValue')} />
                <Field label="Payment">
                  <Select value={form.paymentMode} onChange={setField('paymentMode')}>
                    <option value="Prepaid">Prepaid</option>
                    <option value="COD">Cash on delivery</option>
                  </Select>
                </Field>
                {form.paymentMode === 'COD' && (
                  <Input label="COD amount (₹)" type="number" min="0" value={form.codAmount} onChange={setField('codAmount')} required />
                )}
                <Input label="Internal note" value={form.notes} onChange={setField('notes')} className="md:col-span-2" />
              </div>
            </section>
          </div>
        )}
      </Modal>

      {/* ---------------- Tracking drawer ---------------- */}
      <Modal
        isOpen={Boolean(tracked)}
        onClose={() => setTracked(null)}
        title={tracked?.referenceNumber || 'Tracking'}
        subtitle={
          tracked?.consignmentNumber
            ? `DTDC ${tracked.consignmentNumber} · ${tracked?.patient?.name || ''}`
            : 'Not booked with DTDC yet'
        }
        maxWidth="max-w-2xl"
        footer={
          <>
            {tracked && !['Delivered', 'Cancelled'].includes(tracked.status) && (
              <Button variant="danger" size="sm" onClick={() => setCancelTarget(tracked)}>
                Cancel consignment
              </Button>
            )}
            {tracked?.consignmentNumber && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(buildDtdcTrackingLink(tracked.consignmentNumber), '_blank', 'noopener')}
                icon={<FaExternalLinkAlt className="h-3 w-3" />}
              >
                DTDC site
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => handleTrack(tracked)}
              isLoading={tracking}
              disabled={!tracked?.consignmentNumber}
              icon={<FaSyncAlt className="h-3.5 w-3.5" />}
            >
              Refresh now
            </Button>
          </>
        }
      >
        {tracked && (
          <div className="space-y-5">
            <ShipmentProgress status={tracked.status} />

            <div className="grid grid-cols-2 gap-3 rounded-xl bg-slate-50 p-4 text-sm md:grid-cols-4">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Status</p>
                <p className="font-semibold text-slate-800">{STATUS_LABELS[tracked.status] || tracked.status}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Deliver to</p>
                <p className="font-medium text-slate-800">{tracked.destination?.city || '—'}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Expected</p>
                <p className="font-medium text-slate-800">
                  {tracked.expectedDeliveryDate
                    ? new Date(tracked.expectedDeliveryDate).toLocaleDateString('en-GB')
                    : '—'}
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Last checked</p>
                <p className="font-medium text-slate-800">
                  {tracked.lastTrackedAt ? formatScanTime(tracked.lastTrackedAt) : 'Never'}
                </p>
              </div>
            </div>

            {tracked.lastError && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                {tracked.lastError}
              </div>
            )}

            <div>
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">Scan history</h4>
                <button
                  onClick={() => copyPatientLink(tracked)}
                  className="text-xs font-medium text-sky-600 hover:text-sky-700"
                >
                  Copy patient tracking link
                </button>
              </div>
              <TrackingTimeline
                events={tracked.trackingEvents}
                emptyMessage={
                  tracked.consignmentNumber
                    ? 'DTDC has not recorded a scan yet. Try refreshing in a little while.'
                    : 'Book this shipment to start tracking.'
                }
              />
            </div>
          </div>
        )}
      </Modal>

      {/* ---------------- Settings ---------------- */}
      <Modal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        title="Courier Account Settings"
        subtitle="Credentials are saved securely per clinic for automated booking and tracking"
        maxWidth="max-w-2xl"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={submitSettings} isLoading={savingSettings}>Save {settingsProviderTab} settings</Button>
          </>
        }
      >
        <div className="flex gap-2 border-b border-slate-200 pb-2 mb-4">
          {['DTDC', 'India Post'].map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => openSettings(p)}
              className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition ${
                settingsProviderTab === p
                  ? 'bg-sky-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {p}
            </button>
          ))}
        </div>

        {settingsForm && (
          <div className="space-y-5">
            <label className="flex items-center gap-2.5 text-sm text-slate-700 font-medium">
              <input
                type="checkbox"
                checked={settingsForm.enabled}
                onChange={(e) => setSettingsForm((f) => ({ ...f, enabled: e.target.checked }))}
                className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
              />
              Enable {settingsProviderTab} dispatch for this clinic
            </label>

            <section>
              <h4 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500">
                {settingsProviderTab === 'India Post' ? 'India Post API Credentials' : 'DTDC Booking (softdata API)'}
              </h4>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Input
                  label={settingsProviderTab === 'India Post' ? 'Facility / Customer ID' : 'Customer code'}
                  value={settingsForm.customerCode}
                  onChange={(e) => setSettingsForm((f) => ({ ...f, customerCode: e.target.value }))}
                />
                <Input
                  label="API key / Secret"
                  type="password"
                  placeholder={settings?.hasApiKey ? settings.apiKeyMasked : `Paste the ${settingsProviderTab} API key`}
                  value={settingsForm.apiKey}
                  onChange={(e) => setSettingsForm((f) => ({ ...f, apiKey: e.target.value }))}
                  helperText={settings?.hasApiKey ? 'Leave blank to keep the saved key' : null}
                />
                <Field label="Environment">
                  <Select
                    value={settingsForm.environment}
                    onChange={(e) => setSettingsForm((f) => ({ ...f, environment: e.target.value }))}
                  >
                    <option value="live">Live / Production</option>
                    <option value="staging">Staging / Demo</option>
                  </Select>
                </Field>
                <Field label="Default service">
                  <Select
                    value={settingsForm.defaultServiceType}
                    onChange={(e) => setSettingsForm((f) => ({ ...f, defaultServiceType: e.target.value }))}
                  >
                    {settingsProviderTab === 'India Post' ? (
                      <>
                        <option value="SPEED POST">Speed Post (EMS)</option>
                        <option value="REGISTERED PARCEL">Registered Parcel</option>
                        <option value="BUSINESS PARCEL">Business Parcel</option>
                      </>
                    ) : (
                      SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)
                    )}
                  </Select>
                </Field>
              </div>
            </section>

            {settingsProviderTab === 'DTDC' && (
              <section>
                <h4 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500">Tracking API</h4>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Input
                    label="Tracking username"
                    value={settingsForm.trackingUsername}
                    onChange={(e) => setSettingsForm((f) => ({ ...f, trackingUsername: e.target.value }))}
                  />
                  <Input
                    label="Tracking password"
                    type="password"
                    placeholder={settings?.hasTrackingCredentials ? '••••••••' : 'DTDC tracking password'}
                    value={settingsForm.trackingPassword}
                    onChange={(e) => setSettingsForm((f) => ({ ...f, trackingPassword: e.target.value }))}
                    helperText={settings?.hasTrackingCredentials ? 'Leave blank to keep the saved password' : null}
                  />
                </div>
              </section>
            )}

            <section>
              <h4 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500">Pickup address</h4>
              <p className="mb-3 text-xs text-slate-500">Used as the sender origin of every parcel consignment.</p>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {[
                  ['name', 'Clinic name'], ['phone', 'Phone'],
                  ['addressLine1', 'Address line 1'], ['addressLine2', 'Address line 2'],
                  ['city', 'City'], ['state', 'State'], ['pincode', 'Pincode'],
                ].map(([key, label]) => (
                  <Input
                    key={key}
                    label={label}
                    value={settingsForm.origin[key] || ''}
                    onChange={(e) =>
                      setSettingsForm((f) => ({ ...f, origin: { ...f.origin, [key]: e.target.value } }))
                    }
                  />
                ))}
              </div>
            </section>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(cancelTarget)}
        onClose={() => setCancelTarget(null)}
        onConfirm={confirmCancel}
        isLoading={cancelling}
        title="Cancel consignment"
        message={`This asks DTDC to cancel ${cancelTarget?.consignmentNumber || 'this shipment'}. It cannot be undone — you would need to book a new consignment.`}
        confirmText="Cancel consignment"
      />
    </div>
  );
}
